import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useUserDataStore } from "src/hooks/snug/use-userData";
import { useAuth } from "src/hooks/use-auth";
import { SplashScreen } from "src/components/splash-screen";

interface OnboardingGuardProps {
  children: ReactNode;
}

export const OnboardingGuard: FC<OnboardingGuardProps> = (props) => {
  // This is a leftover from when onboarding created the userdata object but now we've moved that forward in the process.
  // But this will still catch cases where userData was not created.
  const { children } = props;
  const { user, isAuthenticated } = useAuth();
  const { handleGetUserData, handleAddOrEditOwnerUserData } =
    useUserDataStore();
  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    let userData = await handleGetUserData(); // Call the function from the hook and set userData
    if (!userData?.ud_id) {
      userData = await handleAddOrEditOwnerUserData({
        ud_id: null, // This is null because we are creating a new user data
        full_name: user?.first_name,
        contact_email: user?.username?.toLocaleLowerCase().trim(),
        // REACT_APP_DEFAULT_SNUG_DOMAIN
        assigned_total_domain: window.location.host.replace(
          "https://",
          process.env.REACT_APP_DEFAULT_SNUG_DOMAIN || ""
        ),
      });
    }

    setChecked(true);
  }, [
    handleAddOrEditOwnerUserData,
    handleGetUserData,
    user?.first_name,
    user?.username,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      check();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!checked) {
    return <SplashScreen />;
  }

  return <>{children}</>;
};

OnboardingGuard.propTypes = {
  children: PropTypes.node,
};
